import { domToCanvas, } from 'modern-screenshot';

/**
 * Viewport-relative rectangle shared by the area-selection overlay and the
 * screenshot capture helper. Lives here (in a .ts module) so the .vue
 * components can import it as a named export — `*.vue` shim only exposes
 * the default export.
 */
export type AreaRect = { x: number; y: number; width: number; height: number };

const DEFAULT_MAX_WIDTH = 1280;
const DEFAULT_QUALITY = 0.7;

/**
 * Re-encode a canvas as a WebP data URL, optionally scaling it down so the
 * resulting payload comfortably fits PostHog's per-event budget.
 */
const canvasToWebpDataUrl = async (
  canvas: HTMLCanvasElement,
  maxWidth: number,
  quality: number,
): Promise<string | null> => {
  let output = canvas;
  if (canvas.width > maxWidth) {
    const scale = maxWidth / canvas.width;
    const scaled = document.createElement('canvas');
    scaled.width = maxWidth;
    scaled.height = Math.round(canvas.height * scale);
    const ctx = scaled.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(canvas, 0, 0, scaled.width, scaled.height);
    output = scaled;
  }

  const blob = await new Promise<Blob | null>(resolve => {
    output.toBlob(resolve, 'image/webp', quality);
  });
  if (!blob) return null;

  return new Promise<string | null>(resolve => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
};

/**
 * Captures a rectangular region of the page as a compressed WebP data URL.
 *
 * Strategy: render the entire viewport via modern-screenshot (which uses
 * SVG foreignObject internally and preserves CSS transforms — the
 * calculator's `-webkit-transform: scale()` zoom container was the exact
 * scenario where html2canvas kept blowing up), then crop the result to
 * the user's selection rect with a plain 2D canvas.
 *
 * @param rect Viewport-relative rectangle (clientX/clientY coords).
 * @param options.maxWidth Cap the output width. Default 1280.
 * @param options.quality WebP quality 0..1. Default 0.7.
 *
 * Returns null on failure. Never throws.
 */
export const captureArea = async (
  rect: AreaRect,
  options: { maxWidth?: number; quality?: number } = {},
): Promise<string | null> => {
  const { maxWidth = DEFAULT_MAX_WIDTH, quality = DEFAULT_QUALITY, } = options;

  // The "is this rect big enough to bother capturing?" guard lives in
  // AreaScreenshotOverlay's onMouseUp — single source of truth, closer
  // to user intent (it also routes to emit('cancel') in that case so
  // captureArea is never even called for too-small rects).

  // Match the device pixel ratio so text doesn't look blurry on hi-DPI
  // displays — capped at 2 to keep payload size manageable.
  const scale = Math.min(window.devicePixelRatio || 1, 2);

  // Render the whole document root. modern-screenshot walks the live
  // DOM into an SVG foreignObject, preserving CSS transforms (incl. the
  // calculator's zoom) so the captured pixels line up with what the
  // user is actually looking at.
  let viewportCanvas: HTMLCanvasElement;
  try {
    viewportCanvas = await domToCanvas(document.documentElement, {
      scale,
      // Let modern-screenshot inline cross-origin images via fetch when
      // CORS allows it; those that don't (some bank logos) just render
      // blank — the rest of the screenshot still works.
      fetch: {
        requestInit: { mode: 'cors', credentials: 'omit', },
      },
      // Drop our own widget chrome from the captured pixels so the
      // screenshot reflects the page, not the popover that triggered
      // the capture.
      filter: (node) => {
        if (!(node instanceof Element)) return true;
        const cls = node.classList;
        if (!cls) return true;
        if (cls.contains('comparator-feedback')) return false;
        if (cls.contains('area-screenshot-overlay')) return false;
        if (cls.contains('comparator-feedback__menu')) return false;
        if (cls.contains('comparator-feedback__overflow-menu')) return false;
        return true;
      },
    });
  } catch (error) {
    console.warn('[useScreenshotCapture] modern-screenshot failed', error);
    return null;
  }

  // Crop the viewport-sized capture to the selection rect. The captured
  // canvas dimensions are (viewport × scale); multiply the user's rect
  // by the same scale to land in the captured canvas's pixel space.
  const cropCanvas = document.createElement('canvas');
  cropCanvas.width = Math.round(rect.width * scale);
  cropCanvas.height = Math.round(rect.height * scale);
  const cropCtx = cropCanvas.getContext('2d');
  if (!cropCtx) return null;
  cropCtx.drawImage(
    viewportCanvas,
    Math.round(rect.x * scale),
    Math.round(rect.y * scale),
    Math.round(rect.width * scale),
    Math.round(rect.height * scale),
    0,
    0,
    cropCanvas.width,
    cropCanvas.height,
  );

  return canvasToWebpDataUrl(cropCanvas, maxWidth, quality);
};

/**
 * Downsamples a user-uploaded image file to the same shape we ship for
 * area screenshots — WebP @ 0.7, capped at 1280 px wide. Keeps the event
 * payload comfortably under PostHog's ~1 MB per-event limit even when the
 * user picks a 3-4 MB phone screenshot.
 */
export const compressUploadedImage = async (
  file: File,
  options: { maxWidth?: number; quality?: number } = {},
): Promise<string | null> => {
  const { maxWidth = DEFAULT_MAX_WIDTH, quality = DEFAULT_QUALITY, } = options;

  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    // decode() resolves once the image is ready to paint without forcing a
    // sync layout (unlike onload). Falls back to onload for older Safari.
    try {
      await img.decode();
    } catch {
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('image load failed'));
      });
    }

    const scale = img.width > maxWidth ? maxWidth / img.width : 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    return await canvasToWebpDataUrl(canvas, maxWidth, quality);
  } catch (error) {
    console.warn('[useScreenshotCapture] compressUploadedImage failed', error);
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
};
